import { ArrowLeft, BookOpen, MessageCircle, Search, Settings, Shield, HelpCircle } from 'lucide-react';
import { useUIStore } from '../stores/uiStore';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';

export function HelpCenterPage() {
  const { setCurrentView } = useUIStore();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button variant="ghost" size="icon" onClick={() => setCurrentView('chat')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-semibold">Hilfecenter</h1>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Introduction */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                KI für Pflege - Benutzerhandbuch
              </CardTitle>
              <CardDescription>
                KI für Pflege ist eine moderne Chat-Anwendung, die speziell für Pflegekräfte entwickelt wurde.
                Sie ermöglicht es, Fragen zu stellen und Unterstützung durch künstliche Intelligenz zu erhalten.
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Anmeldung & Registrierung */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Anmeldung & Registrierung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Registrierung</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Öffnen Sie die Anwendung unter der bereitgestellten URL</li>
                  <li>Klicken Sie auf "Registrieren"</li>
                  <li>Geben Sie Ihre E-Mail-Adresse ein</li>
                  <li>Wählen Sie ein sicheres Passwort (mindestens 8 Zeichen)</li>
                  <li>Optional: Geben Sie Ihren Anzeigenamen ein</li>
                  <li>Klicken Sie auf "Registrieren"</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Anmeldung</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Geben Sie Ihre E-Mail-Adresse ein</li>
                  <li>Geben Sie Ihr Passwort ein</li>
                  <li>Klicken Sie auf "Anmelden"</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Abmeldung</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Klicken Sie unten links auf Ihr Benutzerprofil</li>
                  <li>Wählen Sie "Abmelden"</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Chat-Funktionen */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Chat-Funktionen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Neuen Chat starten</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Klicken Sie in der Seitenleiste auf "Neuer Chat"</li>
                  <li>Ein neuer Chat wird erstellt und automatisch geöffnet</li>
                  <li>Geben Sie Ihre Nachricht in das Textfeld unten ein</li>
                  <li>Drücken Sie Enter oder klicken Sie auf den Senden-Button</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Chat-Verlauf</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Alle Ihre Chats werden in der Seitenleiste angezeigt</li>
                  <li>Die Chats sind nach dem letzten Nachrichtendatum sortiert (neueste oben)</li>
                  <li>Klicken Sie auf einen Chat, um ihn zu öffnen</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Chat umbenennen</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Bewegen Sie die Maus über einen Chat in der Liste</li>
                  <li>Klicken Sie auf das Drei-Punkte-Menü (⋮)</li>
                  <li>Wählen Sie "Umbenennen"</li>
                  <li>Geben Sie den neuen Namen ein</li>
                  <li>Klicken Sie auf "Speichern"</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Chat archivieren</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Bewegen Sie die Maus über einen Chat in der Liste</li>
                  <li>Klicken Sie auf das Drei-Punkte-Menü (⋮)</li>
                  <li>Wählen Sie "Archivieren"</li>
                </ol>
                <p className="text-sm text-muted-foreground mt-1">
                  Archivierte Chats werden aus der Hauptliste entfernt, bleiben aber über die Suche verfügbar.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Archivierten Chat wiederherstellen</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Öffnen Sie die Suche</li>
                  <li>Finden Sie den archivierten Chat (gekennzeichnet mit "Archiviert")</li>
                  <li>Klicken Sie auf das Drei-Punkte-Menü (⋮)</li>
                  <li>Wählen Sie "Wiederherstellen"</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Chat löschen</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Bewegen Sie die Maus über einen Chat in der Liste</li>
                  <li>Klicken Sie auf das Drei-Punkte-Menü (⋮)</li>
                  <li>Wählen Sie "Löschen"</li>
                  <li>Bestätigen Sie die Löschung im Dialogfenster</li>
                </ol>
                <p className="text-sm text-destructive mt-1 font-medium">
                  Achtung: Gelöschte Chats können nicht wiederhergestellt werden!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Suche */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Suche
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Chats durchsuchen</h4>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Klicken Sie in der Seitenleiste auf "Suche"</li>
                  <li>Ein Suchfenster öffnet sich</li>
                  <li>Geben Sie Ihren Suchbegriff ein</li>
                  <li>Die Ergebnisse werden nach Chat-Titeln gefiltert</li>
                  <li>Klicken Sie auf einen Chat, um ihn zu öffnen</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Suchhinweise</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Die Suche durchsucht Chat-Titel</li>
                  <li>Archivierte Chats werden mit einem "Archiviert"-Badge angezeigt</li>
                  <li>Sie können archivierte Chats direkt aus der Suche heraus verwalten</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Benutzermenü */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Benutzermenü
              </CardTitle>
              <CardDescription>
                Das Benutzermenü finden Sie unten links in der Seitenleiste.
                Klicken Sie auf Ihren Namen/Avatar, um es zu öffnen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Einstellungen</h4>
                <p className="text-muted-foreground">
                  Klicken Sie auf "Einstellungen" im Benutzermenü.
                  Hier können Sie Ihr Profil und Ihre Präferenzen anpassen.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Hilfe-Menü</h4>
                <p className="text-muted-foreground mb-3">
                  Im Benutzermenü unter "Hilfe" finden Sie:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>
                    <strong>Hilfecenter:</strong> Allgemeine Hilfe und Dokumentation
                  </li>
                  <li>
                    <strong>Chathilfe:</strong> Öffnet ein kleines Chat-Fenster unten rechts.
                    Stellen Sie hier Fragen zur Bedienung der Anwendung.
                  </li>
                  <li>
                    <strong>AGB und Richtlinien:</strong> Rechtliche Informationen und Nutzungsbedingungen
                  </li>
                  <li>
                    <strong>Fehler melden:</strong> Öffnet ein Formular zum Melden von Problemen
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Tipps & Tricks */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Tipps & Tricks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Effektive Nutzung</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><strong>Klare Fragen stellen:</strong> Je präziser Ihre Frage, desto besser die Antwort</li>
                  <li><strong>Kontext geben:</strong> Beschreiben Sie die Situation, wenn relevant</li>
                  <li><strong>Neue Chats für neue Themen:</strong> Starten Sie einen neuen Chat für ein neues Thema</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Tastenkürzel</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><strong>Enter:</strong> Nachricht senden</li>
                  <li><strong>Shift + Enter:</strong> Zeilenumbruch in der Nachricht</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Chat-Organisation</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Benennen Sie Chats aussagekräftig um</li>
                  <li>Archivieren Sie abgeschlossene Gespräche</li>
                  <li>Nutzen Sie die Suche, um alte Chats schnell zu finden</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Häufige Fragen (FAQ)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-1">Wie sicher sind meine Daten?</h4>
                <p className="text-muted-foreground">
                  Alle Daten werden verschlüsselt übertragen und sicher gespeichert.
                  Ihre Gespräche sind nur für Sie sichtbar.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Kann ich gelöschte Chats wiederherstellen?</h4>
                <p className="text-muted-foreground">
                  Nein, gelöschte Chats sind unwiderruflich entfernt.
                  Nutzen Sie stattdessen die Archivieren-Funktion.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Warum wird mein Chat nicht gespeichert?</h4>
                <p className="text-muted-foreground">
                  Stellen Sie sicher, dass Sie angemeldet sind.
                  Bei Verbindungsproblemen werden Nachrichten zwischengespeichert.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Wie melde ich mich auf einem anderen Gerät an?</h4>
                <p className="text-muted-foreground">
                  Nutzen Sie einfach Ihre E-Mail-Adresse und Ihr Passwort auf dem neuen Gerät.
                  Ihre Chats werden synchronisiert.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Was mache ich, wenn ich mein Passwort vergessen habe?</h4>
                <p className="text-muted-foreground">
                  Kontaktieren Sie den Support über die "Fehler melden"-Funktion.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Support */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Support</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-2">Bei Problemen oder Fragen:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Nutzen Sie zuerst die <strong>Chathilfe</strong> (Benutzermenü → Hilfe → Chathilfe)</li>
                <li>Falls das Problem bestehen bleibt, nutzen Sie <strong>Fehler melden</strong> (Benutzermenü → Hilfe → Fehler melden)</li>
              </ol>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground">
            Stand: Februar 2026
          </p>
        </div>
      </ScrollArea>
    </div>
  );
}
